<template>
  <Modal @backgroundClose="$emit('cancel')">
    <div class="modal modal--selection-conflict">
      <div class="modalwrap2-topline">
        <div class="topline-minizone"></div>

        <Breadcrumps
          :data="[{ title: $t('modalNotSaved:Message:Default:Title') }]"
        />

        <div class="topline-minizone">
          <Button
            text=""
            size="s-standart"
            color="accentlightlines"
            icon="x"
            :is-loading="false"
            :loading-left="true"
            @click="$emit('cancel')"
          />
        </div>
      </div>

      <div class="modalwrap2-content">
        <div class="modalwrap3-text">
          <p class="text-mini conflict-message conflict-message--lead">
            {{ headerText }}
          </p>
          <p class="text-mini conflict-message conflict-message--sub">
            {{ subText }}
          </p>
        </div>

        <div class="conflict-list-wrap">
          <article class="chkset chkset--conflict">
            <div class="blocklinewrap sets-wrap">
              <div
                ref="scrollContainer"
                class="sets-column--full sets-column--scroll"
                @scroll="updateScrollIndicator"
              >
                <div
                  v-for="name in productNames"
                  :key="name"
                  class="conflict-item"
                >
                  <div class="conflict-item__icon vsvg" v-svg="{ name: 'x' }"></div>
                  <div class="conflict-item__text">{{ name }}</div>
                </div>
              </div>
              <div v-if="showScrollIndicator" class="conflict-scroll-indicator">
                <div
                  class="conflict-scroll-indicator__thumb"
                  :style="scrollIndicatorStyle"
                />
              </div>
            </div>
          </article>
        </div>

        <div class="modalwrap3-buttons">
          <Button
            :text="$t('DefenseCombinations:ConfirmCancel')"
            size="wide"
            color="graylines"
            icon="cancel"
            :is-loading="false"
            :loading-left="true"
            @click="$emit('cancel')"
          />

          <Button
            :text="$t('DefenseCombinations:ConfirmReplace')"
            size="wide"
            color="alertlines"
            icon="tick"
            :is-loading="false"
            :loading-left="true"
            @click="$emit('confirm')"
          />
        </div>
      </div>
    </div>
  </Modal>
</template>

<script>
import Button from '@/components/views/Button';
import Breadcrumps from '@/components/views/Breadcrumps';
import Modal from '@/pages/Modal';

export default {
  name: 'ModalSelectionConflict',
  components: { Button, Breadcrumps, Modal },
  data() {
    return {
      showScrollIndicator: false,
      scrollIndicatorHeight: 0,
      scrollIndicatorTop: 0,
    };
  },
  props: {
    productNames: {
      type: Array,
      required: true,
    },
    isCombination: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    headerText() {
      const count = this.productNames.length;
      return this.isCombination
        ? this.$t('DefenseCombinations:ConflictHeader:Combination', { count })
        : this.$t('DefenseCombinations:ConflictHeader:Product', { count });
    },
    subText() {
      return this.isCombination
        ? this.$t('DefenseCombinations:ConflictSub:Combination')
        : this.$t('DefenseCombinations:ConflictSub:Product');
    },
    scrollIndicatorStyle() {
      return {
        height: `${this.scrollIndicatorHeight}px`,
        transform: `translateY(${this.scrollIndicatorTop}px)`,
      };
    },
  },
  mounted() {
    window.addEventListener('resize', this.updateScrollIndicator);
    this.$nextTick(this.updateScrollIndicator);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateScrollIndicator);
  },
  methods: {
    updateScrollIndicator() {
      const container = this.$refs.scrollContainer;

      if (!container) {
        this.showScrollIndicator = false;
        return;
      }

      const { clientHeight, scrollHeight, scrollTop } = container;
      const canScroll = scrollHeight - clientHeight > 1;

      this.showScrollIndicator = canScroll;

      if (!canScroll) {
        this.scrollIndicatorHeight = 0;
        this.scrollIndicatorTop = 0;
        return;
      }

      const trackInset = 12;
      const trackHeight = Math.max(clientHeight - trackInset * 2, 0);
      const thumbHeight = Math.max(
        Math.round((clientHeight / scrollHeight) * trackHeight),
        44
      );
      const maxThumbOffset = Math.max(trackHeight - thumbHeight, 0);
      const maxScrollTop = Math.max(scrollHeight - clientHeight, 1);
      const thumbTop = Math.round((scrollTop / maxScrollTop) * maxThumbOffset);

      this.scrollIndicatorHeight = thumbHeight;
      this.scrollIndicatorTop = thumbTop;
    },
  },
};
</script>

<style lang="scss">
@import '~@/scss/vars.scss';

.modal--selection-conflict {
  padding-top: $marginMini;

  .modalwrap2-topline {
    margin-bottom: $marginMini;
  }

  .modalwrap2-content {
    height: auto;
    justify-content: flex-start;
    align-items: center;
  }

  .modalwrap3-text {
    width: 100%;
    max-width: none;
    padding: 0 $marginMini;
    margin-bottom: $marginMini;
    box-sizing: border-box;
  }

  .modalwrap3-text p {
    margin: 0;
    padding: 0 !important;
    text-align: center;
    color: $colorTextGray;
  }

  .conflict-message {
    font-size: $fontsizeBigButtons;
    line-height: $lineFontsizeBigButtons;
  }

  .conflict-message--lead {
    font-weight: $fontBold;
    margin-bottom: $marginNano;
  }

  .conflict-message--sub {
    max-width: 100%;
    margin: 0 auto;
  }

  .conflict-list-wrap {
    width: 100%;
    display: flex;
    justify-content: center;
    margin-bottom: $marginMini;
  }

  .chkset--conflict {
    width: $widthStandart2x;

    .blocklinewrap {
      width: 100%;
      position: relative;
    }

    .sets-column--full {
      width: 100%;
      margin-bottom: 0;

      & > * {
        margin-top: 0;
        margin-left: 0;
      }
    }

    .sets-column--scroll {
      max-height: 184px;
      overflow-y: scroll;
      scrollbar-gutter: stable;
      scrollbar-width: none;
      -ms-overflow-style: none;
      padding: $marginNano 14px $marginNano 0;

      &::-webkit-scrollbar {
        width: 0;
        height: 0;
        display: none;
      }
    }
  }

  .conflict-scroll-indicator {
    position: absolute;
    top: 12px;
    right: 6px;
    bottom: 12px;
    width: 6px;
    border-radius: $borderRadiusStandart;
    background: $colorGrayLighter;
    pointer-events: none;

    &__thumb {
      width: 100%;
      border-radius: inherit;
      background: $colorAccentLight;
      transition: transform 0.12s ease-out;
    }
  }

  .conflict-item {
    display: flex;
    align-items: center;
    min-height: 0;
    padding: 12px $marginMini;

    &__icon {
      flex-shrink: 0;
      margin-right: $marginMicro;
      width: $iconSizeStandart - 4px;
      min-width: $iconSizeStandart - 4px;
      height: $iconSizeStandart - 4px;

      & svg,
      & svg * {
        fill: $colorLinesError;
      }
    }

    &__text {
      flex: 1;
      font-size: $fontsizeStandart;
      line-height: $lineFontsizeStandart;
      color: $colorTextGray;
      text-align: left;
    }
  }

  .modalwrap3-buttons {
    width: 100%;
    display: flex;
    justify-content: space-between;
    gap: $marginStandart;

    & > *:first-child {
      margin-right: 0;
    }
  }
}

@media screen and (max-width: 1800px) {
  .modal--selection-conflict {
    padding-top: $marginMini-k1600;

    .modalwrap2-topline {
      margin-bottom: $marginMini-k1600;
    }

    .modalwrap3-text {
      max-width: none;
      padding: 0 $marginMini-k1600;
      margin-bottom: $marginMini-k1600;
    }

    .conflict-message {
      font-size: $fontsizeBigButtons-k1600;
      line-height: $lineFontsizeBigButtons-k1600;
    }

    .conflict-message--lead {
      margin-bottom: $marginNano-k1600;
    }

    .conflict-message--sub {
      max-width: 100%;
    }

    .conflict-list-wrap {
      margin-bottom: $marginMini-k1600;
    }

    .chkset--conflict {
      width: $widthStandart2x-k1600;

      .sets-column--scroll {
        max-height: 164px;
        padding: $marginNano-k1600 12px $marginNano-k1600 0;
      }
    }

    .conflict-scroll-indicator {
      top: 10px;
      right: 5px;
      bottom: 10px;
      width: 5px;
    }

    .conflict-item {
      padding: 10px $marginMini-k1600;

      &__icon {
        margin-right: $marginMicro-k1600;
        width: $iconSizeStandart-k1600 - 3px;
        min-width: $iconSizeStandart-k1600 - 3px;
        height: $iconSizeStandart-k1600 - 3px;
      }

      &__text {
        font-size: $fontsizeStandart-k1600;
        line-height: $lineFontsizeStandart-k1600;
      }
    }

    .modalwrap3-buttons {
      gap: $marginStandart-k1600;
    }
  }
}

@media screen and (max-width: 1420px) {
  .modal--selection-conflict {
    padding-top: $marginMini-k1360;

    .modalwrap2-topline {
      margin-bottom: $marginMini-k1360;
    }

    .modalwrap3-text {
      max-width: none;
      padding: 0 $marginMini-k1360;
      margin-bottom: $marginMini-k1360;
    }

    .conflict-message {
      font-size: $fontsizeBigButtons-k1360;
      line-height: $lineFontsizeBigButtons-k1360;
    }

    .conflict-message--lead {
      margin-bottom: $marginNano-k1360;
    }

    .conflict-message--sub {
      max-width: 100%;
    }

    .conflict-list-wrap {
      margin-bottom: $marginMini-k1360;
    }

    .chkset--conflict {
      width: $widthStandart2x-k1360;

      .sets-column--scroll {
        max-height: 148px;
        padding: $marginNano-k1360 11px $marginNano-k1360 0;
      }
    }

    .conflict-scroll-indicator {
      top: 8px;
      right: 4px;
      bottom: 8px;
      width: 4px;
    }

    .conflict-item {
      padding: 8px $marginMini-k1360;

      &__icon {
        margin-right: $marginMicro-k1360;
        width: $iconSizeStandart-k1360 - 2px;
        min-width: $iconSizeStandart-k1360 - 2px;
        height: $iconSizeStandart-k1360 - 2px;
      }

      &__text {
        font-size: $fontsizeStandart-k1360;
        line-height: $lineFontsizeStandart-k1360;
      }
    }

    .modalwrap3-buttons {
      gap: $marginStandart-k1360;
    }
  }
}
</style>
